import { UserAnPCheck } from "../../../shared/UserAnPCheck.js";
import { ErrorBoundary } from "../../../../components/ErrorBoundary.js";
import { ErrorLoadingRetry } from "../../../../components/ErrorLoadingRetry.js";
import { useRPCServerEventHandler } from "../../../../hooks/useRPCServerEventHandler/index.js";
import { useTabsState } from "../../../../hooks/useTabsState.js";
import { useEvent } from "../../hooks/useEvent.js";
import { RefLinksForRefInteractive } from "../../../shared/RefLinksForRefInteractive/index.js";
import { EventDetails } from "./EventDetails/index.js";
import { EventAssignedUserStatusHistory } from "./components/EventAssignedUserStatusHistory.js";
import { EventDispositionFormBuilder } from "./EventDispositionFormBuilder/index.js";
import { EventInfoFormBuilder } from "./EventInfoFormBuilder/index.js";
import { EventComments } from "../../components/EventComments.js";
import { EditEventAssignedUsers } from "./components/EditEventAssignedUsers.js";
import { EventState } from "./components/EventState.js";
import { ChangeEventStateButtonActionModal } from "./components/ChangeEventStateButtonActionModal.js";
import { useMemo } from "react";
import { Constants, Label, Loading, Tabs, useContainerDimensionsContext } from "@therms/atalaya";
import { jsx, jsxs } from "react/jsx-runtime";
import classnames from "classnames";
//#region src/modules/Events/shared/EventInteractive/index.tsx
/**
* This component is intended to be used in OPS and Dispatch web-apps where Organization Users can interact and
* edit information for the Event.
*/
function EventInteractive({ eventId }) {
	const [tab, setTab] = useTabsState(eventId, "info");
	const { data, loading, refresh } = useEvent({ eventId });
	useRPCServerEventHandler("EventDetailsUpdated", (evt, data) => {
		if (data.eventId === eventId) refresh();
	});
	useRPCServerEventHandler("EventStateUpdated", (evt, data) => {
		if (data.eventId === eventId) refresh();
	});
	const { viewport: { width: viewportWidth } } = useContainerDimensionsContext();
	const isMobileLayout = viewportWidth < Constants.Breakpoint.Sm.width;
	const dispositionCounts = useMemo(() => {
		if (!data || !data.event.assignedUsers.length) return 0;
		const completed = Object.values(data.event.dispositionFormInputByAssignedUserId || {}).length;
		if (data.event.assignedUsers.length === 1) return completed;
		return `${completed}/${data.event.assignedUsers.length}`;
	}, [data]);
	if (!data && loading) return /* @__PURE__ */ jsx("div", {
		className: "p-xl flex justify-center",
		children: /* @__PURE__ */ jsx(Loading, { size: "xl" })
	});
	if (!data) return /* @__PURE__ */ jsx(ErrorLoadingRetry, { onRetry: refresh });
	const { event, eventType } = data;
	const allowEdit = event.state !== "closed";
	return /* @__PURE__ */ jsxs("div", {
		className: "gap-y-sm flex flex-col",
		children: [
			/* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx(EventDetails, {
				allowEdit,
				event,
				eventType,
				refresh
			}) }),
			/* @__PURE__ */ jsx("hr", { className: "border-neutral opacity-40" }),
			/* @__PURE__ */ jsxs("div", {
				className: "flex flex-col",
				children: [/* @__PURE__ */ jsx(Label, {
					className: "mb-sm",
					children: "State"
				}), /* @__PURE__ */ jsxs("div", {
					className: classnames({
						"gap-y-xs flex-col": isMobileLayout,
						"justify-between": !isMobileLayout
					}, "flex"),
					children: [/* @__PURE__ */ jsx(EventState, { event }), /* @__PURE__ */ jsx(UserAnPCheck, {
						check: (user) => (user.anp.role.ops.permissions.createEvents || user.anp.role.dispatch.permissions.createEvents) && event.history?.createdBy === user.id || user.anp.role.ops.permissions.editEvents || user.anp.role.dispatch.permissions.editEvents || event?.assignedUsers.find(({ userId }) => user.id === userId),
						children: /* @__PURE__ */ jsx(ChangeEventStateButtonActionModal, {
							event: data.event,
							eventType: data.eventType,
							refresh,
							fullWidth: isMobileLayout
						})
					})]
				})]
			}),
			allowEdit && /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsxs("div", { children: [/* @__PURE__ */ jsx(Label, {
				className: "mb-3",
				children: "Assignment"
			}), /* @__PURE__ */ jsx(EditEventAssignedUsers, {
				event,
				eventType,
				refresh
			})] }) }),
			/* @__PURE__ */ jsx("div", { className: "py-base" }),
			/* @__PURE__ */ jsxs(Tabs, {
				currentKey: tab,
				initialTabKey: "info",
				onTabClick: setTab,
				children: [
					eventType.formsConfig.infoFormId && /* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "info",
						title: "Info",
						children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx("div", {
							className: "min-h-[15vh]",
							children: /* @__PURE__ */ jsx(EventInfoFormBuilder, {
								allowEdit,
								event,
								eventType,
								refresh
							})
						}) })
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "disposition",
						notifications: dispositionCounts,
						title: "Disposition",
						children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx("div", {
							className: "min-h-[15vh]",
							children: /* @__PURE__ */ jsx(EventDispositionFormBuilder, {
								allowEdit,
								event,
								eventType,
								refresh,
								showAssignedUserStatusHistory: true
							})
						}) })
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "refs",
						title: "Ref Links",
						children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx("div", {
							className: "min-h-[15vh]",
							children: /* @__PURE__ */ jsx(RefLinksForRefInteractive, { forRef: {
								type: "event",
								value: event.id
							} })
						}) })
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "comments",
						notifications: event.comments,
						title: "Comments",
						children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx("div", {
							className: "min-h-[15vh]",
							children: /* @__PURE__ */ jsx(EventComments, { eventId: event.id })
						}) })
					}),
					/* @__PURE__ */ jsx(Tabs.Tab, {
						tabKey: "history",
						title: "History",
						children: /* @__PURE__ */ jsx(ErrorBoundary, { children: /* @__PURE__ */ jsx("div", {
							className: "min-h-[25vh]",
							children: /* @__PURE__ */ jsx(EventAssignedUserStatusHistory, { event })
						}) })
					})
				]
			})
		]
	});
}
//#endregion
export { EventInteractive };

//# sourceMappingURL=index.js.map